import {
  GetUserInfo,
  SetAnnualFootprint,
  SetUserInfo,
  UpdateCurrency,
} from './user';
import { Network } from '@wren/shared';
import { guessCurrency } from '../util/conversions';
import { shouldAutoSetCurrencyFromCountry } from '../util/util';
import { getCalculatorKeysForUser } from '../lib/calculator';
import * as Sentry from '@sentry/react';

let updateFootprintRequestToken = 0;
const latestUpdateFootprintRequestByField = {};
let updateFootprintChain = Promise.resolve();

export function ClearCalculator() {
  return {
    type: 'CLEAR_CALCULATOR',
  };
}

export const SetCalculatorQuestionIndex = (questionIndex) => {
  return (dispatch, getState) => {
    const calculatorKeys = getCalculatorKeysForUser(getState());

    dispatch({
      type: 'SET_CALCULATOR_QUESTION_INDEX',
      questionIndex,
      calculatorKeys,
    });
  };
};

export const ShowNextQuestion = () => {
  return (dispatch, getState) => {
    const calculatorKeys = getCalculatorKeysForUser(getState());
    dispatch({ type: 'SHOW_NEXT_QUESTION', calculatorKeys });
  };
};

export const ShowPreviousQuestion = () => {
  return (dispatch, getState) => {
    const calculatorKeys = getCalculatorKeysForUser(getState());
    dispatch({ type: 'SHOW_PREVIOUS_QUESTION', calculatorKeys });
  };
};

export function SetCountryAverage(countryAverage) {
  return {
    type: 'SET_COUNTRY_AVERAGE',
    countryAverage,
  };
}

export function SetFootprintCountry(country) {
  return {
    type: 'SET_COUNTRY',
    country,
  };
}

export function SetFootprintDefaults(zipDefaults) {
  return {
    type: 'SET_FOOTPRINT_DEFAULTS',
    zipDefaults,
  };
}

export function UpdateUserAnswer(answer) {
  return {
    type: 'UPDATE_USER_ANSWER',
    answer,
  };
}

export function UpdateAnnualTotals(totals) {
  return {
    type: 'UPDATE_ANNUAL_TOTALS',
    totals,
  };
}

function StartFootprintUpdate() {
  return {
    type: 'START_FOOTPRINT_UPDATE',
  };
}

function FinishFootprintUpdate() {
  return {
    type: 'FINISH_FOOTPRINT_UPDATE',
  };
}

export const UpdateFootprint = (body) => (dispatch, getState) => {
  const requestToken = ++updateFootprintRequestToken;
  const updatedFields = Object.keys(body ?? {});
  updatedFields.forEach((field) => {
    latestUpdateFootprintRequestByField[field] = requestToken;
  });

  dispatch(StartFootprintUpdate());
  dispatch(UpdateUserAnswer(body));

  // Serialize network calls so each reads the latest footprintId.
  // When a footprint is >1 day old the backend creates a new row (versioning),
  // changing the ID. Without serialization, concurrent requests race and the
  // second one hits a 403 because it targets the stale ID.
  const networkCall = async () => {
    const { footprintId } = getState().user;
    try {
      const [response, responseBody] = await Network.post(
        `footprints/${footprintId}`,
        body
      );

      if (response.ok) {
        window.posthog?.capture('Client: User Updated Footprint', body);

        dispatch(SetUserInfo({ footprintId: responseBody.footprint.id }));

        const isLatestResponseForUpdatedFields = updatedFields.every(
          (field) => latestUpdateFootprintRequestByField[field] === requestToken
        );
        if (!isLatestResponseForUpdatedFields) {
          return;
        }

        dispatch(UpdateUserAnswer(responseBody.footprint));
        dispatch(
          SetAnnualFootprint(responseBody.footprintBreakdown.grandTotal)
        );
        dispatch(UpdateAnnualTotals(responseBody.footprintBreakdown));
      }
    } finally {
      dispatch(FinishFootprintUpdate());
    }
  };

  updateFootprintChain = updateFootprintChain
    .catch((err) => Sentry.captureException(err))
    .then(networkCall);
  return updateFootprintChain;
};

const dispatchAnswersToReducer = async (dispatch, json) => {
  dispatch(UpdateUserAnswer(json));
  dispatch(SetFootprintCountry(json.country));
  dispatch(GetDefaultFootprint(json.country));
};

export const GetAndSetFootprint = () => async (dispatch, getState) => {
  const { user, calculatorQuestionIndex } = getState();

  if (!user.footprintId) {
    return;
  }

  const [response, responseBody] = await Network.get(
    `footprints/${user.footprintId}`
  );

  if (response.ok) {
    if (!responseBody.footprint.country) {
      dispatch(SetFootprintCountry(null));
      dispatch(SetCalculatorQuestionIndex(0));
      return;
    }
    if (!calculatorQuestionIndex) {
      dispatch(SetCalculatorQuestionIndex(1));
    }
    await dispatchAnswersToReducer(dispatch, responseBody.footprint);

    dispatch(SetAnnualFootprint(responseBody.footprint.annualFootprint));
    dispatch(UpdateAnnualTotals(responseBody.footprintBreakdown));
  }
};

export function GetDefaultFootprint(country) {
  return async (dispatch, getState) => {
    const { userAnswers } = getState().calculator;

    const query = {
      country: country ?? userAnswers.country ?? 'United States',
    };

    if (userAnswers.calculationType === 'household') {
      if (userAnswers.householdAdults) {
        query.adults = userAnswers.householdAdults ?? 1;
      }

      if (userAnswers.householdChildren) {
        query.children = userAnswers.householdChildren ?? 0;
      }
    }

    const [response, responseBody] = await Network.get(
      'footprints/default',
      query
    );

    if (response.ok) {
      dispatch(SetFootprintDefaults(responseBody.zipDefaults));
      const annualFootprint =
        responseBody.zipDefaults.annualFootprint.grandTotal;
      dispatch(SetCountryAverage(annualFootprint));
    }
  };
}

export function SetCountry(country) {
  return async (dispatch, getState) => {
    // this allows us to speed up perceived performance by moving to the
    // next question immediately instead of waiting for the footprint to change
    dispatch(SetFootprintCountry(country));

    const { user } = getState();
    await dispatch(UpdateFootprint({ country }));
    await dispatch(GetDefaultFootprint(country));

    const currentCurrencyCode = guessCurrency(country);

    if (shouldAutoSetCurrencyFromCountry(user)) {
      dispatch(UpdateCurrency(currentCurrencyCode));
    } else {
      dispatch(GetUserInfo());
    }
  };
}

export function UpdateHouseholdCar(carId, attributes) {
  return async (dispatch) => {
    const [response] = await Network.post(
      `household-cars/${carId}`,
      attributes
    );

    if (response.ok) {
      dispatch(GetAndSetFootprint());
    }
  };
}
